import { useAtom } from 'jotai';
import { useCallback, useState } from 'react';

import { stickyboardAskAtom } from '@frontend/atoms/stickyboardAtoms';
import { HYPATIA_URL } from '@frontend/envConfig';
import { useStreamExecute } from '@frontend/features/agents/hooks';
import { executeSpecializedAgent } from '@frontend/features/agents/services/specialized-agent';
import type { ExecutionCompleteData, StreamEvent } from '@frontend/features/agents/types/streaming';
import { useInstanceId } from '@frontend/hooks';

import { useStewardAgent } from './useStewardAgent';

/**
 * "Ask the Steward" — drives the Stickyboard search bar against the Steward, a
 * no-parameter conversation agent. The launch sequence (verified against the
 * live agent) is:
 *
 *   Turn 1 (init): POST /execute { parameters: {} } — a no-param agent can't
 *     carry the query on turn 1 (messages require a memory_id), so an empty
 *     execute opens the thread and returns the memory_id. Its empty-input
 *     response is discarded.
 *   Turn 2+ (query + follow-ups): stream-execute { messages, memory_id } with
 *     the prior etag as If-Match — streamed into `answer`.
 *
 * Only the latest answer is kept (the full transcript lives in the agent's
 * memory thread). Reset drops the memory_id so the next query opens a fresh
 * thread.
 */
export function useAskSteward() {
  const { instanceId } = useInstanceId();
  const { agentUuid, ready } = useStewardAgent();
  const [ask, setAsk] = useAtom(stickyboardAskAtom);
  const [error, setError] = useState<string | null>(null);

  const { execute, abort, isStreaming } = useStreamExecute({
    onChunk: (event: StreamEvent) => {
      if (event.event_type === 'response_chunk') {
        const text = (event.data.content as string) ?? '';
        if (text) setAsk(prev => ({ ...prev, answer: prev.answer + text }));
      }
    },
    onComplete: (data: ExecutionCompleteData) => {
      setAsk(prev => ({ ...prev, memoryId: data.memory_id, etag: data.etag }));
    },
    onError: err => setError(err.message || 'The Steward could not respond.'),
  });

  const submit = useCallback(
    async (raw: string) => {
      const query = raw.trim();
      if (!query || !agentUuid || !instanceId || isStreaming) return;
      setError(null);
      // Only-last-answer: clear the previous answer as the new one begins.
      setAsk(prev => ({ ...prev, answer: '' }));

      let memoryId = ask.memoryId;
      let etag = ask.etag ?? undefined;

      // Turn 1 — open the thread. A no-param agent rejects `messages` without a
      // memory_id, so an empty `parameters` execute creates it.
      if (!memoryId) {
        try {
          const execUrl = `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/${agentUuid}/execute`;
          const { data, etag: initEtag } = await executeSpecializedAgent(execUrl, {}, false)();
          memoryId = data.memory_id;
          etag = initEtag ?? data.etag ?? undefined;
          setAsk(prev => ({ ...prev, memoryId: memoryId ?? null, etag: etag ?? null }));
        } catch {
          setError('Could not start a conversation with the Steward.');
          return;
        }
      }

      // Turn 2+ — deliver the query (and every follow-up) over SSE.
      execute({
        instanceId,
        agentId: agentUuid,
        parameters: {},
        messages: [{ text: query }],
        memoryId,
        etag,
      });
    },
    [agentUuid, instanceId, isStreaming, ask.memoryId, ask.etag, execute, setAsk]
  );

  const reset = useCallback(() => {
    abort();
    setAsk({ memoryId: null, etag: null, answer: '' });
    setError(null);
  }, [abort, setAsk]);

  return {
    answer: ask.answer,
    isStreaming,
    error,
    submit,
    reset,
    ready,
    hasThread: !!ask.memoryId,
  };
}
